import React, { useState, useMemo } from 'react';
import type { OverseasShipment, Supplier } from '../../../types';
import PlusIcon from '../../icons/PlusIcon';
import EyeIcon from '../../icons/EyeIcon';
import EditIcon from '../../icons/EditIcon';
import TruckIcon from '../../icons/TruckIcon';
import XCircleIcon from '../../icons/XCircleIcon';
import MagnifyingGlassIcon from '../../icons/MagnifyingGlassIcon';
import TrashIcon from '../../icons/TrashIcon';
import ClipboardCheckIcon from '../../icons/ClipboardCheckIcon';
import VarianceReportModal from './VarianceReportModal';

interface ShipmentListProps {
  shipments: OverseasShipment[];
  suppliers: Supplier[];
  onCreateNew: () => void;
  onEdit?: (shipment: OverseasShipment) => void;
  onView: (shipment: OverseasShipment) => void;
  onReceive: (shipment: OverseasShipment) => void;
  onCancel: (shipmentId: string) => void;
  onRemove?: (shipmentId: string) => void;
}

const ShipmentList: React.FC<ShipmentListProps> = ({
  shipments,
  suppliers,
  onCreateNew,
  onEdit,
  onView,
  onReceive,
  onCancel,
  onRemove,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'arrived' | 'receiving' | 'completed' | 'cancelled'>('all');
  const [filterSupplier, setFilterSupplier] = useState<number | 'all'>('all');
  const [selectedShipmentForReport, setSelectedShipmentForReport] = useState<OverseasShipment | null>(null);

  const filteredShipments = useMemo(() => {
    return shipments.filter(shipment => {
      // Search filter
      if (searchTerm && !shipment.shipmentNumber.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Status filter
      if (filterStatus !== 'all' && shipment.status !== filterStatus) {
        return false;
      }

      // Supplier filter
      if (filterSupplier !== 'all' && shipment.supplierId !== filterSupplier) {
        return false;
      }

      return true;
    });
  }, [shipments, searchTerm, filterStatus, filterSupplier]);

  const getStatusBadge = (status: OverseasShipment['status']) => {
    const badges = {
      pending: 'bg-gray-100 text-gray-800',
      arrived: 'bg-blue-100 text-blue-800',
      receiving: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return badges[status] || badges.pending;
  };

  const getStatusLabel = (status: OverseasShipment['status']) => {
    const labels = {
      pending: 'Pending',
      arrived: 'Arrived',
      receiving: 'Receiving',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSupplierName = (supplierId: number) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier?.name || `Supplier #${supplierId}`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Overseas Shipments</h2>
        <button
          onClick={onCreateNew}
          className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center"
        >
          <PlusIcon className="w-5 h-5 mr-2" /> Create Manifest
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by shipment number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="arrived">Arrived</option>
            <option value="receiving">Receiving</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Supplier Filter */}
          <select
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          >
            <option value="all">All Suppliers</option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Shipments Table */}
      {filteredShipments.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
          <TruckIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 mb-2">
            {shipments.length === 0
              ? 'No shipments yet. Create your first manifest to get started.'
              : 'No shipments match your filters.'}
          </p>
          {shipments.length === 0 && (
            <button
              onClick={onCreateNew}
              className="mt-4 bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300"
            >
              Create First Manifest
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shipment #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Arrival</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredShipments.map((shipment) => (
                  <tr key={shipment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{shipment.shipmentNumber}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{getSupplierName(shipment.supplierId)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(shipment.status)}`}>
                        {getStatusLabel(shipment.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(shipment.expectedArrivalDate)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{shipment.items?.length || 0} items</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(shipment.dateCreated)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => onView(shipment)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                        {(shipment.status === 'completed' || shipment.status === 'receiving') && (
                          <button
                            onClick={() => setSelectedShipmentForReport(shipment)}
                            className="text-purple-600 hover:text-purple-900"
                            title="View Variance Report"
                          >
                            <ClipboardCheckIcon className="w-5 h-5" />
                          </button>
                        )}
                        {shipment.status === 'pending' && (
                          <button
                            onClick={() => onReceive(shipment)}
                            className="text-green-600 hover:text-green-900"
                            title="Mark as Arrived"
                          >
                            <TruckIcon className="w-5 h-5" />
                          </button>
                        )}
                        {(shipment.status === 'arrived' || shipment.status === 'receiving') && (
                          <button
                            onClick={() => onReceive(shipment)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Receive Shipment"
                          >
                            <TruckIcon className="w-5 h-5" />
                          </button>
                        )}
                        {shipment.status !== 'completed' && shipment.status !== 'cancelled' && (
                          <button
                            onClick={() => onCancel(shipment.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Cancel"
                          >
                            <XCircleIcon className="w-5 h-5" />
                          </button>
                        )}
                        {shipment.status === 'completed' && onRemove && (
                          <button
                            onClick={() => onRemove(shipment.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Remove Shipment (Reverse Inventory Changes)"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {selectedShipmentForReport && (
        <VarianceReportModal
          shipment={selectedShipmentForReport}
          onClose={() => setSelectedShipmentForReport(null)}
        />
      )}
    </div>
  );
};

export default ShipmentList;
